import React from 'react';

import cn from 'classnames';
import { orderBy } from 'lodash';
import PropTypes from 'prop-types';
import { RiLoader5Line } from 'react-icons/ri';
import { HiChevronDown } from 'react-icons/hi';

import { Menu, Text, Badge } from 'modules/common';

const splitArray = (array) => {
  const x = array.slice(0, 20);
  const y = array.slice(20, array.length);
  return [x, y];
};

const dataToRender = (data) => {
  const [first, second] = splitArray(data);
  return { first, second };
};

const FormInputSelect = React.forwardRef((props, ref) => {
  const {
    id,
    leadingItem,
    name,
    error,
    label,
    withHiddenLabel,
    value: $value,
    isGroup,
    options,
    disabled,
    required,
    onChange,
    badgeProps,
    isSorted,
    className,
    isLoading,
    withValue,
    isMultiple,
    placeholder: $placeholder,
    loadOptions,
    isSearchable,
    sizeClassName,
    onSetFieldValue,
    labelClassName,
    containerClassName,
    hideDropdown,
    optionClassName,
    errorKeys,
    isOptional,
    errorClassName,
    helperText,
  } = props;
  const value = Array.isArray($value) ? $value : [$value];
  const clickRef = React.useRef(null);
  const isMounted = React.useRef(true);
  const placeholder = $placeholder || `Select ${(label || '')?.toLowerCase()}`;
  const itemProps = {
    iconProps: {
      className: 'h-4 w-4',
    },
  };

  const [isShow, setIfShow] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [searchValue, setSearchValue] = React.useState('');

  const [menuOption, setMenuOption] = React.useState([]);

  const handleOnSelectValue = (val) => (e) => {
    if (e) e.preventDefault();

    if (isMultiple && Array.isArray(value)) {
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, [...value, val]);
        return;
      }

      if (typeof onChange === 'function')
        onChange(
          (prev) => ({ ...prev, [name]: [...value, val] }),
          'increase',
          val
        );
      return;
    }

    if (isMultiple && !Array.isArray(value)) {
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, [val]);
        return;
      }
      if (typeof onChange === 'function')
        onChange((prev) => ({ ...prev, [name]: [val] }), 'increase', val);
      return;
    }

    if (!isMultiple && isShow) {
      setTimeout(() => {
        setIfShow(false);
      }, 100);
    }

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, val);
      return;
    }

    if (typeof onChange === 'function') {
      onChange((prev) => ({ ...prev, [name]: val }));
    }
  };

  const handleOnUnSelectValue = (val) => (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newSelectedOption = value?.filter(
      (item) => `${item?.value}` !== `${val?.value}`
    );
    if (isMultiple) {
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, newSelectedOption);
        return;
      }
      onChange(
        (prev) => ({ ...prev, [name]: newSelectedOption }),
        'decrease',
        val
      );
      return;
    }

    setTimeout(() => {
      setIfShow(false);
    }, 50);

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, newSelectedOption[0] || '');
      return;
    }
    if (typeof onChange === 'function') {
      onChange(
        (prev) => ({ ...prev, [name]: newSelectedOption[0] || '' }),
        'decrease',
        val
      );
    }
  };

  const handleOnSearchMenu = (v) => {
    const val = v()?.[name];

    setSearchValue(val);

    if (loadOptions) {
      setLoading(true);
      setTimeout(() => {
        loadOptions(val, (res) => {
          if (!isMounted.current) return;
          setMenuOption(res);
          setLoading(false);
        });
      }, 300);
      return;
    }

    if (val) {
      const x = options.filter(
        (item) => item?.label?.toLowerCase().indexOf(val.toLowerCase()) > -1
      );
      setMenuOption(x);
    }

    if (val?.length <= 0) {
      setMenuOption(options);
    }
  };

  const handleOnSetActive = (e) => {
    e.preventDefault();
    if (!isShow) {
      setIfShow(true);
      return;
    }
    setTimeout(() => {
      setIfShow(false);
    }, 100);
  };

  React.useEffect(() => {
    let timer;
    if (options && !loadOptions) {
      setMenuOption(options);
      setLoading(false);
    }
    if (!options && loadOptions) {
      setLoading(true);
      loadOptions('', (res) => {
        if (!isMounted.current) return;
        setMenuOption(res);
        setLoading(false);
      });
      timer = setTimeout(() => {
        setLoading(false);
      }, 5000);
    }
    return () => {
      clearInterval(timer);
    };
    // Check this if component get infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  React.useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  React.useEffect(() => {
    const escFunction = (e) => {
      if (e.keyCode === 27) {
        setTimeout(() => {
          setIfShow(false);
        }, 100);
      }
    };

    const handleClearToggle = (e) => {
      try {
        if (clickRef.current && !clickRef.current.contains(e.target)) {
          setTimeout(() => {
            setIfShow(false);
          }, 100);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err, ' here');
      }
    };

    if (isShow) {
      document.addEventListener('keydown', escFunction, false);
      document.addEventListener('mousedown', handleClearToggle, false);
    }
    return () => {
      document.removeEventListener('keydown', escFunction, false);
      document.removeEventListener('mousedown', handleClearToggle, false);
    };
  }, [id, isShow, ref]);

  const sortedMenu = isSorted
    ? orderBy(menuOption, 'label', 'asc')
    : menuOption;

  const { first, second } = dataToRender(value);

  const sortedSelectedMenu = orderBy(value, 'label', 'asc');

  return (
    <div className="w-full relative" ref={clickRef}>
      <div
        className={cn(
          'form-container transition duration-300 ease-in-out group',
          containerClassName,
          {
            'form-container': !error,
            'form-container-error': error,
          }
        )}
      >
        {withHiddenLabel ? (
          <div className="ds-form-label opacity-0">RO</div>
        ) : (
          <>
            {label && (
              <label
                className={cn(
                  'ds-form-label flex items-center',
                  labelClassName
                )}
                htmlFor={id ?? name}
              >
                <div className="flex-1">
                  {label}{' '}
                  {required ? (
                    <span className="text-utility-danger-base">*</span>
                  ) : (
                    ''
                  )}
                </div>
                {isOptional && <div className="font-normal">Optional</div>}
              </label>
            )}
          </>
        )}

        <div className="relative w-full">
          <div
            className={cn(
              'transition duration-300 ease-in-out gap-[10px]',
              {
                'ds-form-input': !error,
                'ds-form-input-error': error,
                'ds-form-input-disabled': disabled,
              },
              {
                'border-form-active': isShow,
              },
              {
                'cursor-not-allowed bg-gray-100': disabled,
                'cursor-pointer': !disabled,
              },
              {
                '!py-[9px]': isMultiple,
              },
              className
            )}
            role="presentation"
            onClick={!disabled ? handleOnSetActive : () => {}}
          >
            {leadingItem && (
              <div
                className={cn(
                  'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                  {
                    'form-icon': !disabled,
                    'form-icon-disabled': disabled,
                  }
                )}
              >
                {typeof leadingItem === 'function'
                  ? leadingItem(itemProps)
                  : leadingItem}
              </div>
            )}

            {!withValue ? (
              <div className="flex items-center">
                <span>{label || placeholder}</span>
                {value?.length && isMultiple ? (
                  <div
                    className={cn('space-x-2', {
                      'has-tooltip': value?.length && isMultiple,
                    })}
                  >
                    <span className="font-semibold tex-sm">:</span>
                    <span className="px-3 py-1 rounded-md text-sm font-semibold bg-gray-200">
                      {first.length}
                      {second.length ? `+` : ''}
                    </span>
                    <span className="tooltip rounded shadow-tooltip p-2 bg-gray-900 w-60 bg-opacity-80 text-white mt-5 text-sm">
                      {sortedSelectedMenu?.map((x) => ` ${x?.label}`).join()}
                    </span>
                  </div>
                ) : (
                  <span className="invisible">RO</span>
                )}
              </div>
            ) : (
              <div
                className={cn({
                  truncate: !isMultiple,
                })}
              >
                <div className="flex flex-wrap flex-shrink-0 gap-ds-4">
                  <>
                    {isMultiple && value?.length ? (
                      value?.map((item, key) => (
                        <Badge
                          {...badgeProps}
                          key={item?.value}
                          label={item?.label}
                          withLeading={false}
                          type={
                            errorKeys?.length > 0 && errorKeys?.includes(key)
                              ? 'danger'
                              : badgeProps?.type || 'default'
                          }
                          id="remove-item"
                          onClose={
                            disabled ? () => {} : handleOnUnSelectValue(item)
                          }
                          // disabled={disabled}
                          className={optionClassName}
                        />
                      ))
                    ) : (
                      <>
                        {value?.[0]?.label || placeholder ? (
                          <Text className="truncate" type="body">
                            {value?.[0]?.label || placeholder}
                          </Text>
                        ) : (
                          <span className="text-xs invisible">RO</span>
                        )}
                      </>
                    )}
                  </>
                </div>
              </div>
            )}

            <div className="ml-auto h-0 flex items-center">
              {(loading || isLoading) && !isShow ? (
                <RiLoader5Line className="h-5 w-5 p-0 animate-spin" />
              ) : (
                <HiChevronDown className="h-5 w-5 p-0" />
              )}
            </div>
          </div>
        </div>

        <Menu
          options={sortedMenu}
          onChange={(item, isSelected, e) => {
            if (!isMultiple) {
              return handleOnSelectValue(item)(e);
            }

            return !isSelected
              ? handleOnSelectValue(item)(e)
              : handleOnUnSelectValue(item)(e);
          }}
          isMultiple={isMultiple}
          disabled={disabled}
          isGroup={isGroup}
          isOpen={isShow && !hideDropdown}
          sizeClassName={sizeClassName}
          isSearchable={isSearchable}
          id={id}
          placeholder={placeholder}
          label={label}
          onSearch={handleOnSearchMenu}
          searchValue={searchValue}
          name={name}
          className={cn({
            'mt-2': isShow && !hideDropdown,
          })}
          menuProps={{
            menuItemProps: {
              leadingType: 'select',
            },
          }}
        />
      </div>

      {(Boolean(error) || Boolean(helperText)) && (
        <Text
          type="caption"
          className={cn(
            'mt-ds-6',
            {
              'ds-form-helper': !error,
              'ds-form-helper-error': error,
              'ds-form-helper-disabled': disabled,
            },
            errorClassName
          )}
        >
          <span>{error || helperText || ''}</span>
          <span className="form-error-badge" />
        </Text>
      )}
    </div>
  );
});

FormInputSelect.defaultProps = {
  value: '',
  leadingItem: false,
  error: false,
  className: '',
  label: 'Name',
  isGroup: false,
  options: false,
  isSorted: true,
  onChange: false,
  required: false,
  disabled: false,
  withValue: true,
  isLoading: false,
  id: 'select-menu',
  isMultiple: false,
  labelClassName: '',
  loadOptions: false,
  isSearchable: false,
  onSetFieldValue: false,
  isOptional: false,
  hideDropdown: false,
  withHiddenLabel: false,
  containerClassName: '',
  optionClassName: '',
  sizeClassName: 'w-full',
  placeholder: '',
  badgeProps: {},
  errorKeys: [],
  errorClassName: '',
  helperText: '',
};

FormInputSelect.propTypes = {
  id: PropTypes.string,
  badgeProps: PropTypes.instanceOf(Object),
  label: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  isGroup: PropTypes.bool,
  errorClassName: PropTypes.string,
  required: PropTypes.bool,
  withValue: PropTypes.bool,
  withHiddenLabel: PropTypes.bool,
  isMultiple: PropTypes.bool,
  isSearchable: PropTypes.bool,
  hideDropdown: PropTypes.bool,
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  isSorted: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(Object),
  ]),
  name: PropTypes.string.isRequired,
  sizeClassName: PropTypes.string,
  onChange: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  loadOptions: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  options: PropTypes.oneOfType([PropTypes.instanceOf(Array), PropTypes.bool]),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  isOptional: PropTypes.bool,
  helperText: PropTypes.string,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  optionClassName: PropTypes.string,
  errorKeys: PropTypes.instanceOf(Array),
};

export default FormInputSelect;
